import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Wallet, Download, FileText, CreditCard, Sparkles, Calendar, TrendingUp } from "lucide-react";
import { api, API, getToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import MyEventSidebar from "./MyEventSidebar";

const TECHNICAL_RE = /^(test[_\s-]|iter\d+|fixture|demo[_\s-]|debug|__)/i;
const cleanName = (s) => {
  if (!s) return "Votre mariage";
  const c = String(s).replace(/^\s*(test[_\s-]+|demo[_\s-]+)/i, "").replace(/\s+[a-f0-9]{6,}$/i, "").trim();
  return TECHNICAL_RE.test(c) ? "Votre mariage" : c;
};
const fmtEur = (n) => `${(Number(n) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }); } catch { return iso; } };

const DOC_LABEL = { devis: "Devis", facture: "Facture", facture_acompte: "Facture d'acompte" };
const STATUS_LABEL = { brouillon: "Brouillon", envoye: "Envoyé", signe: "Signé", paid: "Payé", payee: "Payée", annule: "Annulé" };

export default function MyEventWallet() {
  const { eventId } = useParams();
  const { user } = useAuth();
  const [w, setW] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [err, setErr] = useState(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    if (!eventId) return;
    api.get(`/next/myevent/${eventId}/dashboard`).then(r => setDashboard(r.data)).catch(() => {});
    api.get(`/next/myevent/${eventId}/wallet`)
      .then(r => setW(r.data))
      .catch(e => setErr(e?.response?.status || 500));
  }, [eventId]);

  if (!user) return <Navigate to="/connexion" replace />;
  if (err === 403) return <Err title="Accès refusé" text="Vous n'êtes pas membre de cet événement." testid="wallet-forbidden" />;
  if (err === 404) return <Err title="Événement introuvable" text="Cet événement n'existe pas." testid="wallet-notfound" />;
  if (!w) return <div className="min-h-screen bg-black text-white p-8"><p className="text-white/50 italic">Chargement…</p></div>;

  const b = w.budget || {};
  const s = w.subscription;
  const ev = dashboard?.event || {};
  const couple = cleanName(ev.name);
  const progressPct = b.total_ttc > 0 ? Math.round((b.total_paid / b.total_ttc) * 100) : 0;
  const canPayDeposit = !!(b.next_deposit && b.next_deposit.payable && b.next_deposit.amount > 0);

  const downloadPdf = async (docId, number) => {
    try {
      const token = getToken();
      const r = await fetch(`${API}/next/myevent/${eventId}/wallet/documents/${docId}/pdf`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `${number || docId}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Téléchargement impossible."); }
  };

  const payDeposit = async () => {
    if (!canPayDeposit) return;
    setPaying(true);
    try {
      // Reuse the LEGACY Stripe LIVE flow — never modified.
      const r = await api.post(`/me/documents/${b.next_deposit.doc_id}/deposit-checkout`, {
        success_url: `${window.location.origin}/myevent/${eventId}/wallet?paid=1`,
        cancel_url: `${window.location.origin}/myevent/${eventId}/wallet`,
      });
      const url = r?.data?.checkout_url || r?.data?.url;
      if (url) { window.location.assign(url); return; }
      toast.error("Lien de paiement indisponible.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de démarrer le paiement.");
    } finally { setPaying(false); }
  };

  return (
    <div className="min-h-screen bg-black text-white pb-20" data-testid="myevent-wallet">
      <header className="border-b border-[#DCB457]/15 bg-black/95 backdrop-blur sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-4 flex items-center justify-between">
          <Link to={`/myevent/${eventId}`} data-testid="wallet-back" className="text-xs uppercase tracking-widest text-white/60 hover:text-[#DCB457] inline-flex items-center gap-2">
            <ArrowLeft size={13} /> MyEvent
          </Link>
          <span className="text-[10px] uppercase tracking-[0.24em] text-[#DCB457] border border-[#DCB457]/40 px-2 py-1">MyWallet</span>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-8 lg:pt-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <MyEventSidebar />
        <main className="lg:col-span-9 min-w-0 space-y-8">
          <section>
            <p className="eyebrow">MyEvent · Module</p>
            <h1 className="font-serif-luxe text-4xl md:text-5xl mt-3">Votre <span className="italic gold-text">budget.</span></h1>
            <p className="mt-3 text-white/60 text-sm md:text-base">Suivez votre budget mariage et vos échéances en un coup d'œil.</p>
            {ev.date && <p className="mt-1 text-[#DCB457] text-sm" data-testid="wallet-event-summary">{couple} — {fmtDate(ev.date)}</p>}
          </section>

          {/* SECTION 1 : BUDGET DU MARIAGE */}
          <section data-testid="wallet-budget-section">
            <p className="eyebrow flex items-center gap-2"><Wallet size={12} /> Budget du mariage</p>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4" data-testid="wallet-kpis">
              <Kpi label="Total" value={fmtEur(b.total_ttc)} />
              <Kpi label="Payé" value={fmtEur(b.total_paid)} accent />
              <Kpi label="Restant" value={fmtEur(b.remaining)} warn={b.remaining > 0} />
              <Kpi label="Jours restants" value={b.days_to_event != null ? (b.days_to_event >= 0 ? `J − ${b.days_to_event}` : `J + ${Math.abs(b.days_to_event)}`) : "—"} />
            </div>

            {/* Barre de progression */}
            {b.total_ttc > 0 && (
              <div className="mt-6" data-testid="wallet-progress">
                <div className="flex items-baseline justify-between mb-2">
                  <p className="text-[10px] uppercase tracking-widest text-white/50">Avancement paiement</p>
                  <p className="text-[#DCB457] font-serif-luxe text-lg">{progressPct}%</p>
                </div>
                <div className="h-1 bg-white/10 overflow-hidden">
                  <div className="h-full bg-[#DCB457] transition-all duration-700" style={{ width: `${progressPct}%` }} />
                </div>
              </div>
            )}

            {/* Prochaine échéance */}
            {canPayDeposit && (
              <div className="card-luxe p-6 mt-6 border-[#DCB457]/40" data-testid="wallet-next-deposit">
                <div className="flex items-start justify-between flex-wrap gap-4">
                  <div>
                    <p className="eyebrow flex items-center gap-2"><Calendar size={11} /> Prochaine échéance</p>
                    <p className="font-serif-luxe text-2xl mt-2">Acompte à régler</p>
                    <p className="text-sm text-white/60 mt-1">Devis {b.next_deposit.number || ""} · <span className="gold-text">{fmtEur(b.next_deposit.amount)}</span></p>
                  </div>
                  <button onClick={payDeposit} disabled={paying} data-testid="wallet-pay-deposit"
                    className="inline-flex items-center gap-2 px-5 py-3 bg-[#DCB457] text-black hover:bg-[#DCB457]/90 text-xs uppercase tracking-widest disabled:opacity-50">
                    <CreditCard size={14} /> {paying ? "Redirection…" : "Payer mon acompte"}
                  </button>
                </div>
                <p className="text-[10px] text-white/40 mt-3 italic">Paiement sécurisé Stripe — vous serez redirigé.</p>
              </div>
            )}

            {/* Documents */}
            <div className="mt-6" data-testid="wallet-documents">
              <p className="eyebrow mb-3 flex items-center gap-2"><FileText size={11} /> Vos documents</p>
              {(!b.documents || b.documents.length === 0) ? (
                <div className="card-luxe p-6 text-white/50 italic text-sm">Aucun document disponible pour l'instant.</div>
              ) : (
                <div className="card-luxe divide-y divide-[#DCB457]/10">
                  {b.documents.map(d => (
                    <div key={d.id} data-testid={`wallet-doc-${d.id}`} className="flex items-center justify-between px-5 py-3 gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-white/85 truncate">
                          {DOC_LABEL[d.doc_type] || d.doc_type} <span className="text-white/45">·</span> {d.number || d.id}
                        </p>
                        <p className="text-[10px] uppercase tracking-widest text-white/40 mt-0.5">
                          {STATUS_LABEL[d.status] || d.status || ""} · {d.total_ttc ? fmtEur(d.total_ttc) : "—"}
                        </p>
                      </div>
                      <button onClick={() => downloadPdf(d.id, d.number)} data-testid={`wallet-doc-pdf-${d.id}`}
                        className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#DCB457] hover:underline">
                        <Download size={11} /> PDF
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Contrat */}
            {b.contrat && (
              <div className="card-luxe p-5 mt-6" data-testid="wallet-contrat">
                <p className="eyebrow">Contrat</p>
                <p className="font-serif-luxe text-xl mt-2">{b.contrat.numero || "Contrat de prestation"}</p>
                <p className="text-xs text-white/55 mt-1">
                  {b.contrat.date_signature && `Signé le ${fmtDate(b.contrat.date_signature)}`}
                  {b.contrat.montant && ` · ${fmtEur(b.contrat.montant)}`}
                </p>
                <p className="text-[10px] uppercase tracking-widest text-white/40 mt-1">{b.contrat.statut}</p>
              </div>
            )}
          </section>

          {/* SECTION 2 : ABONNEMENT MYANIMATION (séparé, jamais additionné) */}
          {s && (
            <section data-testid="wallet-subscription-section">
              <div className="border-t border-[#DCB457]/15 pt-8">
                <p className="eyebrow flex items-center gap-2"><Sparkles size={11} /> Abonnement MyAnimation</p>
                <div className="card-luxe p-5 mt-4">
                  <div className="flex items-baseline justify-between flex-wrap gap-3">
                    <p className="font-serif-luxe text-2xl" data-testid="wallet-sub-name">{s.plan_name || "—"}</p>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${s.status === "active" ? "text-[#DCB457] border-[#DCB457]/50" : "text-white/50 border-white/25"}`}>
                      {s.status === "active" ? "Actif" : s.status === "pending" ? "En attente" : (s.status || "—")}
                    </span>
                  </div>
                  <p className="text-sm text-white/60 mt-2">
                    {s.amount ? fmtEur(s.amount) : "—"} · Paiement {s.payment_status || "—"}
                  </p>
                  <p className="text-[10px] uppercase tracking-widest text-white/40 mt-2">
                    Cet abonnement ne fait pas partie du budget mariage.
                  </p>
                </div>
              </div>
            </section>
          )}
        </main>
      </div>
    </div>
  );
}

function Kpi({ label, value, accent, warn }) {
  return (
    <div className="card-luxe p-4">
      <p className="eyebrow">{label}</p>
      <p className={`font-serif-luxe text-2xl mt-2 ${accent ? "gold-text" : warn ? "text-amber-300" : ""}`}>{value}</p>
    </div>
  );
}
function Err({ title, text, testid }) {
  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-8">
      <div className="max-w-md text-center" data-testid={testid}>
        <p className="eyebrow">MyWallet</p>
        <h1 className="font-serif-luxe text-3xl mt-3 mb-3">{title}</h1>
        <p className="text-white/60 text-sm">{text}</p>
      </div>
    </div>
  );
}
